import { useEffect, useRef } from "react";

interface UsePollingOptions {
    enabled: boolean;
    interval: number;
    initialDelay: number;
}

/**
 * Generic polling hook that handles start/stop lifecycle and cleanup
 */
export const usePolling = (
    fetchFn: (isActive: () => boolean) => Promise<boolean>,
    options: UsePollingOptions
): void => {
    const { enabled, interval, initialDelay } = options;
    const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isPollingActiveRef = useRef(false);

    // Keep fetchFn in ref to avoid stale closures
    const fetchFnRef = useRef(fetchFn);
    useEffect(() => {
        fetchFnRef.current = fetchFn;
    });

    useEffect(() => {
        if (!enabled) return;

        const isActive = (): boolean => isPollingActiveRef.current;

        const scheduleNextPoll = (): void => {
            if (!isActive()) return;

            pollingTimeoutRef.current = setTimeout(async () => {
                if (!isActive()) return;

                await fetchFnRef.current(isActive);

                if (isActive()) {
                    scheduleNextPoll();
                }
            }, interval);
        };

        const startPolling = async (): Promise<void> => {
            if (isActive()) return;

            isPollingActiveRef.current = true;
            await fetchFnRef.current(isActive);

            if (isActive()) {
                scheduleNextPoll();
            }
        };

        const stopPolling = (): void => {
            isPollingActiveRef.current = false;
            if (pollingTimeoutRef.current) {
                clearTimeout(pollingTimeoutRef.current);
                pollingTimeoutRef.current = null;
            }
        };

        pollingTimeoutRef.current = setTimeout(startPolling, initialDelay);

        return stopPolling;
    }, [enabled, interval, initialDelay]);
};
